import type { DraftJson } from '@apps/card/routes/CardCustomizer/DraftJson.types';
import { formatEnvelopeDesignName } from '@apps/card/routes/CardCustomizer/steps/Envelope/envelopeTemplateUtils';

export const ENVELOPE_PRODUCT_TYPE = 'envelope';

type EnvelopeOrderCandidate = Readonly<{
  product?: Readonly<{ productType?: string | null }> | null;
  cardDraftJSONBlob?: unknown;
}>;

/**
 * Whether a placed order is a standalone envelope, for the downstream screens that render one.
 *
 * Prefers the order's PRODUCT: the carrier template's category is bookkeeping that exists only to
 * satisfy a non-null FK (§2.1 of doc/standalone-envelopes/PLAN.md), so it must not be the signal.
 * Falls back to the draft blob's own discriminator — an envelope and no card front, mirroring
 * card_service's `isEnvelopeOnlyCard` — because several order queries select `cardDraftJSONBlob`
 * but not `product`, and adding the field to them would mean a repo-wide `yarn codegen` that is
 * not additive (it removed an unrelated field last run; see PR 3a).
 */
export const isEnvelopeOrder = (order: EnvelopeOrderCandidate | null | undefined): boolean => {
  if (!order) return false;
  if (order.product?.productType) return order.product.productType === ENVELOPE_PRODUCT_TYPE;

  const card = (order.cardDraftJSONBlob as DraftJson | null | undefined)?.card;
  // Deliberately `!card.front?.layers` and not `...?.length`: card_service's `isEnvelopeOnlyCard`
  // tolerates a stray `front: {}` but still counts `front: { layers: [] }` as a card, and the two
  // must not disagree about what an order is.
  return !!card?.envelope && !card.front?.layers;
};

/**
 * The design name under an envelope order's heading — "Traditional" — or `undefined` for anything
 * else, so callers keep their card branch.
 *
 * From the order's own blob, never its template: the carrier is shared by every envelope order,
 * so naming from it would label them all identically (§2.1). Legacy drafts store the bare
 * `CardEnvelopeDesign` and new ones the full themeId; `formatEnvelopeDesignName` handles both.
 */
export const getEnvelopeOrderDesignName = (order: EnvelopeOrderCandidate | null | undefined): string | undefined => {
  if (!isEnvelopeOrder(order)) return undefined;

  const design = (order?.cardDraftJSONBlob as DraftJson | null | undefined)?.card?.envelope?.design;
  return design ? formatEnvelopeDesignName(design) : undefined;
};
